import { Check, Copy, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { favoritesApi, type FavoriteType } from '../../services/favoritesApi';
import { memberRewardsApi } from '../../services/memberRewardsApi';
import { withMemberCode } from '../../utils/memberCode';
import {
  savePendingAction,
  takePendingAction,
  type PendingAction,
} from '../../utils/pendingAction';
import Sheet from '../ui/Sheet';
import { btnGhost, btnGhostSm, btnPrimary } from '../ui/redesign';
import { useToast } from '../ui/toastContext';

import { SiteActionsContext, type LoginActionLabel, type SiteActions } from './siteActionsContext';

/**
 * Site-wide actions behind the new cards and detail pages (PRD §5.9):
 * - favorites: signed-in users only; the saved ids load once and every card reads them,
 * - the login prompt that starts the sign-in chain and remembers the action,
 * - sharing: the system share panel on phones, otherwise copy + an instruction dialog.
 */

/** Includes the hash: the homepage keeps its stage and filters there. */
function currentPath(location: { pathname: string; search: string; hash: string }): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Pages whose buyers can use a member new user promo code (docs/prd-member-rewards.md §5.1):
 * activities, recorded courses and passes. Picks are not booked on YOYO Club.
 */
function acceptsMemberCode(path: string): boolean {
  return (
    path.startsWith('/activity/') || path.startsWith('/courses/') || path.startsWith('/passes/')
  );
}

/** Phones and tablets get the system share panel; desktops get the copy dialog. */
function canUseSystemShare(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(pointer: coarse)').matches === true
  );
}

export function SiteActionsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // --- favorites -----------------------------------------------------------
  const [saved, setSaved] = useState<{ activity: Set<string>; pick: Set<string> }>({
    activity: new Set(),
    pick: new Set(),
  });
  const savedRef = useRef(saved);
  savedRef.current = saved;

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setSaved({ activity: new Set(), pick: new Set() });
      return;
    }
    let cancelled = false;
    favoritesApi
      .listIds()
      .then((ids) => {
        if (!cancelled) setSaved({ activity: new Set(ids.activity), pick: new Set(ids.pick) });
      })
      .catch(() => {
        // Not fatal: hearts show as unsaved and a tap still saves.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading]);

  const setOne = useCallback((type: FavoriteType, id: string, on: boolean) => {
    setSaved((prev) => {
      const next = new Set(prev[type]);
      if (on) next.add(id);
      else next.delete(id);
      return { ...prev, [type]: next };
    });
  }, []);

  const writeFavorite = useCallback(
    async (type: FavoriteType, id: string, on: boolean) => {
      setOne(type, id, on);
      try {
        if (on) await favoritesApi.add(type, id);
        else await favoritesApi.remove(type, id);
        toast(on ? t('favorite.added') : t('favorite.removed'));
      } catch {
        setOne(type, id, !on);
        toast(t('favorite.failed'));
      }
    },
    [setOne, toast, t]
  );

  // --- login prompt ---------------------------------------------------------
  const [loginPrompt, setLoginPrompt] = useState<{
    action: PendingAction;
    label: LoginActionLabel;
  } | null>(null);

  const requireLogin = useCallback(
    (action: PendingAction, label: LoginActionLabel) => {
      if (isAuthenticated) return true;
      setLoginPrompt({ action, label });
      return false;
    },
    [isAuthenticated]
  );

  const goToAuth = (page: 'login' | 'register') => {
    if (!loginPrompt) return;
    const returnTo = currentPath(location);
    savePendingAction(loginPrompt.action, returnTo);
    setLoginPrompt(null);
    navigate(`/${page}?redirect=${encodeURIComponent(returnTo)}`);
  };

  const toggleFavorite = useCallback(
    (type: FavoriteType, id: string) => {
      if (!requireLogin({ type: 'favorite', targetType: type, targetId: id }, 'favorite')) return;
      void writeFavorite(type, id, !savedRef.current[type].has(id));
    },
    [requireLogin, writeFavorite]
  );

  // After sign-in the visitor lands back on the page; finish a remembered save once.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const action = takePendingAction(currentPath(location), 'favorite');
    if (action) void writeFavorite(action.targetType, action.targetId, true);
  }, [authLoading, isAuthenticated, location, writeFavorite]);

  // --- share -----------------------------------------------------------------
  const [shareState, setShareState] = useState<{
    url: string;
    copied: boolean;
    memberCode: string | null;
  } | null>(null);

  // The signed-in member's promo code, looked up once so a share can attach it
  // without a request (the system share panel needs the tap's user activation).
  // 'none' = no code yet (created on the first share), 'off' = paused or unknown.
  const memberCode = useRef<{ state: 'unknown' | 'none' | 'off' } | { state: 'on'; code: string }>({
    state: 'unknown',
  });
  useEffect(() => {
    memberCode.current = { state: 'unknown' };
    if (authLoading || !isAuthenticated) return;
    let cancelled = false;
    memberRewardsApi
      .getCode()
      .then((res) => {
        if (cancelled) return;
        memberCode.current = !res
          ? { state: 'none' }
          : res.codeActive
            ? { state: 'on', code: res.code }
            : { state: 'off' };
      })
      .catch(() => {
        // Leave it unknown: the next share tries again.
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  /** Re-read the code's state so an admin pause or resume shows on the next share. */
  const refreshMemberCode = useCallback(() => {
    memberRewardsApi
      .getCode()
      .then((res) => {
        memberCode.current = !res
          ? { state: 'none' }
          : res.codeActive
            ? { state: 'on', code: res.code }
            : { state: 'off' };
      })
      .catch(() => {
        // Keep what we had; the next share tries again.
      });
  }, []);

  /** A confirmed, usable code to attach, or null to share plainly (§5.1). */
  const resolveMemberCode = useCallback(async (): Promise<string | null> => {
    const known = memberCode.current;
    if (known.state === 'on') return known.code;
    if (known.state === 'off') return null;
    try {
      const res = await memberRewardsApi.createCode();
      memberCode.current = res.codeActive ? { state: 'on', code: res.code } : { state: 'off' };
      return res.codeActive ? res.code : null;
    } catch {
      return null;
    }
  }, []);

  const copy = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const share = useCallback(
    (path: string, title?: string) => {
      const send = (code: string | null) => {
        const url = `${window.location.origin}${code ? withMemberCode(path, code) : path}`;
        const text = code ? t('share.memberCodeText', { code }) : undefined;
        if (canUseSystemShare()) {
          navigator.share({ url, title, text }).catch((err: unknown) => {
            // The visitor closing the panel is not an error; anything else falls back.
            if (err instanceof DOMException && err.name === 'AbortError') return;
            void copy(url).then((copied) => setShareState({ url, copied, memberCode: code }));
          });
          return;
        }
        void copy(url).then((copied) => setShareState({ url, copied, memberCode: code }));
      };

      if (!isAuthenticated || !acceptsMemberCode(path)) {
        send(null);
        return;
      }
      // Attach synchronously when the code is already known, so the system share
      // panel opens within the tap; otherwise fetch (or create) it first.
      const known = memberCode.current;
      if (known.state === 'on' || known.state === 'off') {
        send(known.state === 'on' ? known.code : null);
        refreshMemberCode();
      } else void resolveMemberCode().then(send);
    },
    [copy, isAuthenticated, refreshMemberCode, resolveMemberCode, t]
  );

  const linkInput = useRef<HTMLInputElement>(null);
  const copyAgain = async () => {
    if (!shareState) return;
    if (await copy(shareState.url)) {
      setShareState({ ...shareState, copied: true });
      toast(t('share.copiedToast'));
    } else {
      linkInput.current?.select();
      toast(t('share.selectToast'));
    }
  };

  const isFavorite = useCallback((type: FavoriteType, id: string) => saved[type].has(id), [saved]);

  const value = useMemo<SiteActions>(
    () => ({ isFavorite, toggleFavorite, requireLogin, share }),
    [isFavorite, toggleFavorite, requireLogin, share]
  );

  return (
    <SiteActionsContext.Provider value={value}>
      {children}

      <Sheet
        open={loginPrompt !== null}
        onClose={() => setLoginPrompt(null)}
        closeLabel={t('share.close')}
        title={
          loginPrompt
            ? t('loginPrompt.title', { action: t(`loginPrompt.actions.${loginPrompt.label}`) })
            : ''
        }
      >
        <p className="mt-1.5 text-[14.5px] text-ink-2">{t('loginPrompt.body')}</p>
        <div className="mt-[18px] flex flex-wrap gap-2.5">
          <button
            type="button"
            className={`${btnPrimary} flex-1`}
            onClick={() => goToAuth('login')}
          >
            {t('loginPrompt.login')}
          </button>
          <button
            type="button"
            className={`${btnGhost} flex-1`}
            onClick={() => goToAuth('register')}
          >
            {t('loginPrompt.register')}
          </button>
        </div>
      </Sheet>

      <Sheet
        open={shareState !== null}
        onClose={() => setShareState(null)}
        closeLabel={t('share.close')}
        title={shareState?.copied ? t('share.copiedTitle') : t('share.copyTitle')}
        leading={
          <div className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-primary-pale text-primary">
            {shareState?.copied ? (
              <Check className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Share2 className="h-6 w-6" aria-hidden="true" />
            )}
          </div>
        }
      >
        <p className="mt-1.5 text-[14.5px] text-ink-2">
          {shareState?.copied ? t('share.copiedBody') : t('share.copyBody')}
        </p>
        {shareState?.memberCode && (
          <p className="mt-2.5 rounded-[12px] bg-primary-pale px-3 py-2 text-[13.5px] text-primary-dark">
            {t('share.memberCodeHint', { code: shareState.memberCode })}{' '}
            <Link
              to="/dashboard?tab=rewards"
              onClick={() => setShareState(null)}
              className="font-semibold underline"
            >
              {t('share.memberCodeRules')}
            </Link>
          </p>
        )}
        <ol className="mt-3.5 grid gap-2">
          {[t('share.step1'), t('share.step2'), t('share.step3')].map((step, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-ink-2">
              <span className="mt-px grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-primary-pale font-ui text-xs font-semibold text-primary-dark">
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
        <div className="mt-3.5 flex items-center gap-2 rounded-[12px] border border-line bg-warm-white py-1.5 pl-3 pr-1.5">
          <input
            ref={linkInput}
            readOnly
            value={shareState?.url ?? ''}
            aria-label={t('share.linkLabel')}
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink-2 outline-none"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className={btnGhostSm} onClick={copyAgain}>
            <Copy className="h-4 w-4" aria-hidden="true" />
            {shareState?.copied ? t('share.copyAgain') : t('share.copyLink')}
          </button>
        </div>
        <p className="mt-2.5 text-[12.5px] text-mute">{t('share.mobileHint')}</p>
        <div className="mt-[18px] flex">
          <button
            type="button"
            className={`${btnPrimary} flex-1`}
            onClick={() => setShareState(null)}
          >
            {t('share.done')}
          </button>
        </div>
      </Sheet>
    </SiteActionsContext.Provider>
  );
}
